import { useState } from "react";
import { toast } from "react-toastify";

export interface ProductionPlanPrintData {
  date: string; // Main date (saveToDate)
  items: Array<{
    itemId: string;
    displayName: string;
    plannedQty: number;
    productId: string;
    itemType: string;
    category: string;
    portionSize: string;
    unitPrice: number;
    abbreviation?: string;
    classFilterTag?: string;
  }>;
  totalPlanned: number;
  tierTotals: {
    bronze: number;
    gold: number;
    platinum: number;
  };
  restaurantId: string;
  restaurantName?: string;
  sourceDate?: string;
  isTemplateMode?: boolean;
  isSourceMode?: boolean;
}

const useProductionPlanPrint = () => {
  const [isPrinting, setIsPrinting] = useState(false);

  const generatePrintableContent = (data: ProductionPlanPrintData): string => {
    const { 
      date, 
      items, 
      totalPlanned,
      sourceDate,
      isTemplateMode = false,
      isSourceMode = false,
    } = data;
    
    // Filter items with quantity > 0
    const plannedItems = items.filter(item => item.plannedQty > 0);
    
    // Group items by tier
    const itemsByTier: Record<string, typeof plannedItems> = {
      platinum: [],
      gold: [],
      bronze: [],
      "no-tier": [],
    };

    plannedItems.forEach(item => {
      const tier = item.classFilterTag || "no-tier";
      if (itemsByTier[tier]) {
        itemsByTier[tier].push(item);
      } else {
        itemsByTier["no-tier"].push(item);
      }
    });

    // Sort items within each tier by display name
    Object.keys(itemsByTier).forEach(tier => {
      itemsByTier[tier].sort((a, b) => a.displayName.localeCompare(b.displayName));
    });

    const formattedDate = new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    // Format source date if available
    const formattedSourceDate = sourceDate 
      ? new Date(sourceDate).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : null;

    // Create header based on mode
    let headerTitle = "DAILY PRODUCTION PLAN";
    let dateInfo = formattedDate;
    
    if (isSourceMode && sourceDate) {
      headerTitle = "PRODUCTION PLAN TEMPLATE";
      dateInfo = `
        <div style="display: flex; flex-direction: column; gap: 4px; margin-top: 5px;">
          <div> ${formattedSourceDate}</div>
         
        </div>
      `;
    } else if (isTemplateMode && sourceDate) {
      headerTitle = "PRODUCTION PLAN TEMPLATE";
      dateInfo = `
        <div style="display: flex; flex-direction: column; gap: 4px; margin-top: 5px;">
          <div> ${formattedDate}</div>
         
        </div>
      `;
    }

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Production Plan - ${date}</title>
          <meta charset="UTF-8">
          <style>
            @media print {
              @page {
                size: A4 portrait;
                margin: 10mm;
              }
              body {
                font-family: Arial, sans-serif;
                font-size: 11px;
                line-height: 1.3;
                color: #000;
              }
            }
            
            body {
              font-family: Arial, sans-serif;
              font-size: 11px;
              line-height: 1.3;
              color: #000;
              margin: 0;
              padding: 10px;
            }
            
            .header {
              text-align: center;
              margin-bottom: 10px;
              border-bottom: 2px solid #000;
              padding-bottom: 8px;
            }
            
            .header h1 {
              margin: 0 0 5px 0;
              font-size: 18px;
              font-weight: bold;
            }
            
            .header-info {
              display: flex;
              flex-direction: column;
              align-items: center;
              font-size: 10px;
              margin-bottom: 10px;
            }
            
            .date-info {
              background-color: #f5f5f5;
              padding: 8px 12px;
              border-radius: 4px;
              margin-top: 5px;
              width: 100%;
              max-width: 400px;
            }
            
            .date-info-item {
              display: flex;
              justify-content: space-between;
              margin: 2px 0;
            }
            
            .date-info-label {
              font-weight: bold;
              color: #000;
            }
            
            .date-info-value {
              color: #000;
            }
            
            .total-info {
              text-align: center;
              font-weight: bold;
              font-size: 12px;
              margin: 8px 0;
              padding: 6px;
              background-color: #f0f8ff;
              border-radius: 3px;
              border: 1px solid #d1ecf1;
            }
            
            .table-container {
              width: 100%;
              overflow: hidden;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 5px;
              font-size: 10px;
            }
            
            th {
              background-color: #2c3e50;
              color: white;
              font-weight: bold;
              padding: 6px 4px;
              text-align: left;
              border: 1px solid #ddd;
            }
            
            td {
              padding: 5px 4px;
              border: 1px solid #ddd;
              vertical-align: top;
            }
            
            /* Different background for each tier group */
            .platinum-group tr {
              background-color: #f9f9f9;
            }
            
            .gold-group tr {
              background-color: #fffef0;
            }
            
            .bronze-group tr {
              background-color: #fff8f0;
            }
            
            .no-tier-group tr {
              background-color: #f9f9f9;
            }
            
            /* Alternate row colors within groups */
            .platinum-group tr:nth-child(even) {
              background-color: #f0f0f0;
            }
            
            .gold-group tr:nth-child(even) {
              background-color: #fffde7;
            }
            
            .bronze-group tr:nth-child(even) {
              background-color: #fff3e0;
            }
            
            .no-tier-group tr:nth-child(even) {
              background-color: #f0f0f0;
            }
            
            .product-name {
              font-weight: 500;
            }
            
            .sku {
              font-family: 'Courier New', monospace;
              font-size: 9px;
            }
            
            .quantity {
              text-align: center;
              font-weight: bold;
            }
            
            .price {
              text-align: right;
              font-family: 'Courier New', monospace;
            }
            
            .size {
              text-align: center;
            }
            
            .footer {
              margin-top: 15px;
              padding-top: 8px;
              border-top: 1px solid #ddd;
              font-size: 9px;
              color: #666;
              text-align: center;
            }
            
            .no-items {
              text-align: center;
              padding: 20px;
              font-style: italic;
              color: #999;
            }
            
            /* Tier separator - subtle visual cue */
            .tier-separator {
              height: 2px;
              background-color: #e0e0e0;
              margin: 5px 0;
            }
            
            /* Source mode indicator */
            .source-indicator {
              background-color: #d4edda;
              border: 1px solid #c3e6cb;
              color: #155724;
              padding: 6px 10px;
              border-radius: 4px;
              margin: 10px 0;
              text-align: center;
              font-size: 10px;
              font-weight: bold;
            }
            
            @media print {
              .no-print {
                display: none !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${headerTitle}</h1>
            ${dateInfo}
          
          </div>
          
          <div class="total-info">
            Total Planned Items: ${totalPlanned} packs
          </div>
          
          <div class="table-container">
            ${
              plannedItems.length > 0
                ? `
                  <table>
                    <thead>
                      <tr>
                        <th style="width: 45%;">PRODUCT NAME</th>
                        <th style="width: 15%;">SKU</th>
                        <th style="width: 15%;">PRICE (LKR)</th>
                        <th style="width: 15%;">PLANNED QTY</th>
                      </tr>
                    </thead>
                    <tbody>
                      <!-- Platinum Tier Items -->
                      ${itemsByTier.platinum && itemsByTier.platinum.length > 0 
                        ? itemsByTier.platinum.map(
                            (item) => `
                            <tr class="platinum-group">
                              <td class="product-name">${item.displayName}</td>
                              <td class="sku">${item.productId}</td>
                              <td class="price">${item.unitPrice?.toFixed(2)}</td>
                              <td class="quantity">${item.plannedQty}</td>
                            </tr>
                          `
                          ).join("")
                        : ""
                      }
                      
                      <!-- Gold Tier Items -->
                      ${itemsByTier.gold && itemsByTier.gold.length > 0 
                        ? (itemsByTier.platinum && itemsByTier.platinum.length > 0 ? '<tr><td colspan="4" class="tier-separator"></td></tr>' : '') +
                          itemsByTier.gold.map(
                            (item) => `
                            <tr class="gold-group">
                              <td class="product-name">${item.displayName}</td>
                              <td class="sku">${item.productId}</td>
                              <td class="price">${item.unitPrice?.toFixed(2)}</td>
                              <td class="quantity">${item.plannedQty}</td>
                            </tr>
                          `
                          ).join("")
                        : ""
                      }
                      
                      <!-- Bronze Tier Items -->
                      ${itemsByTier.bronze && itemsByTier.bronze.length > 0 
                        ? ((itemsByTier.platinum && itemsByTier.platinum.length > 0) || (itemsByTier.gold && itemsByTier.gold.length > 0) 
                            ? '<tr><td colspan="4" class="tier-separator"></td></tr>' 
                            : '') +
                          itemsByTier.bronze.map(
                            (item) => `
                            <tr class="bronze-group">
                              <td class="product-name">${item.displayName}</td>
                              <td class="sku">${item.productId}</td>
                              <td class="price">${item.unitPrice?.toFixed(2)}</td>
                              <td class="quantity">${item.plannedQty}</td>
                            </tr>
                          `
                          ).join("")
                        : ""
                      }
                      
                      <!-- No Tier Items -->
                      ${itemsByTier["no-tier"] && itemsByTier["no-tier"].length > 0 
                        ? ((itemsByTier.platinum && itemsByTier.platinum.length > 0) || (itemsByTier.gold && itemsByTier.gold.length > 0) || (itemsByTier.bronze && itemsByTier.bronze.length > 0) 
                            ? '<tr><td colspan="4" class="tier-separator"></td></tr>' 
                            : '') +
                          itemsByTier["no-tier"].map(
                            (item) => `
                            <tr class="no-tier-group">
                              <td class="product-name">${item.displayName}</td>
                              <td class="sku">${item.productId}</td>
                              <td class="price">${item.unitPrice?.toFixed(2)}</td>
                              <td class="quantity">${item.plannedQty}</td>
                            </tr>
                          `
                          ).join("")
                        : ""
                      }
                    </tbody>
                  </table>
                `
                : '<div class="no-items">No items planned for this date</div>'
            }
          </div>
          
          <div class="footer">
            <div>Generated: ${new Date().toLocaleString()}</div>
           
          </div>
          
          <script>
            // Auto-print immediately when page loads
            window.addEventListener('load', function() {
              setTimeout(function() {
                window.print();
                // Close window after a delay
                setTimeout(function() {
                  window.close();
                }, 100);
              }, 50);
            });
          </script>
        </body>
      </html>
    `;
  };

  const printProductionPlan = async (data: ProductionPlanPrintData) => {
    setIsPrinting(true);
    
    try {
      const printContent = generatePrintableContent(data);
      
      // Create a new window for printing
      const printWindow = window.open("", "_blank", "width=800,height=600");
      if (!printWindow) {
        throw new Error("Please allow popups to print.");
      }
      
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      // Focus the window
      printWindow.focus();
      
      // Set a timeout to reset printing state
      setTimeout(() => {
        setIsPrinting(false);
      }, 1000);
      
    } catch (error) {
      console.error("Print error:", error);
      toast.error("Failed to print. Please check your popup settings.");
      setIsPrinting(false);
    }
  };

  return {
    printProductionPlan,
    isPrinting,
  };
};

export default useProductionPlanPrint;